"use client";

import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from "@microsoft/signalr";
import { useEffect, useRef, useState } from "react";
import { Message } from "../types/models/conversation";
import { NotificationCourse } from "../types/models/notification-course";

/**
 * Canal temps réel de l'application.
 *
 * Il est né pour la messagerie et en porte encore le nom, mais il transporte
 * aussi le suivi des courses poussé au back-office : une seconde connexion
 * coûterait au navigateur un flux SSE permanent de plus pour le même besoin.
 *
 * ## Pourquoi le hub passe par `/backend-api` et non par l'API en direct
 *
 * L'access token Keycloak vit dans un cookie HttpOnly : le bundle client ne le
 * voit jamais (cf. `kms-service-provider`). Un hub branché directement sur
 * l'API devrait donc se faire livrer un token par une route serveur, ce qui
 * rouvrirait exactement ce que ce cookie ferme.
 *
 * On passe donc par le proxy same-origin déjà en place, qui injecte lui-même
 * l'en-tête Authorization. Ce proxy est un route handler Next : il relaie du
 * HTTP, pas une montée en WebSocket. Les transports sont donc restreints aux
 * deux transports HTTP de SignalR — SSE pour le flux descendant, long polling
 * en repli. Bénéfice de bord : le temps réel vise toujours le même backend que
 * les appels REST (en dev, `API_URL` et `NEXT_PUBLIC_API_URL` divergent), et
 * l'expiration du token ne casse plus le flux, le proxy repiochant un token
 * frais à chaque reconnexion.
 *
 * Pour repasser en WebSocket il faudrait : une route serveur livrant le token à
 * `accessTokenFactory`, l'origine du front dans le CORS de l'API, et
 * `proxy-read-timeout` relevé sur l'ingress nginx.
 */

/** Adresse du hub, derrière le proxy serveur qui porte l'authentification. */
const URL_HUB = "/backend-api/hubs/conversations";

/** Paliers de reconnexion, en millisecondes, puis palier de croisière. */
const PALIERS_RECONNEXION = [0, 2_000, 5_000, 10_000, 30_000];
const PALIER_MAX_MS = 30_000;

/**
 * Reconnexion sans abandon : le tableau de paliers de SignalR finit par rendre
 * la main, et une messagerie qui ne revient plus après une coupure longue
 * (portable en veille, tunnel, coupure serveur) ne se distingue pas d'une
 * messagerie en panne.
 */
const politiqueReconnexion = {
  nextRetryDelayInMilliseconds: ({ previousRetryCount }: { previousRetryCount: number }) =>
    PALIERS_RECONNEXION[previousRetryCount] ?? PALIER_MAX_MS,
};

export type EtatTempsReel = "hors-ligne" | "connexion" | "connecte";

export type EvenementSaisie = {
  idConversation: number;
  idContact: number;
};

type Handlers = {
  /** Un message vient d'être diffusé, sur n'importe lequel des fils de l'utilisateur. */
  onMessage: (message: Message) => void;
  /** L'entête d'un fil a changé (participants, passage en lecture seule, ...). */
  onConversationMiseAJour: (idConversation: number) => void;
  /** Quelqu'un est en train d'écrire. Éphémère : rien n'est persisté. */
  onSaisie: (evenement: EvenementSaisie) => void;
  /**
   * Une course a bougé : réservée, prise par un chauffeur, changement de
   * statut. Le serveur ne diffuse ces évènements qu'aux gestionnaires — un
   * client connecté n'en reçoit aucun, il n'y a donc rien à filtrer ici.
   */
  onNotificationCourse: (notification: NotificationCourse) => void;
  /**
   * Le flux vient d'être rétabli après une coupure. Les messages postés
   * pendant le trou n'ont été diffusés à personne : c'est ici qu'on les
   * rattrape (`?depuis=` sur les fils ouverts, rechargement de la boîte).
   */
  onReconnexion: () => void;
};

/**
 * Ouvre le canal tant que `actif` est vrai, et le referme sinon (déconnexion,
 * démontage). Les handlers sont lus au travers d'une ref : les redéfinir à
 * chaque rendu ne relance pas la connexion.
 */
export const useConversationHub = (actif: boolean, handlers: Handlers) => {
  const [etat, setEtat] = useState<EtatTempsReel>("hors-ligne");
  const connexionRef = useRef<HubConnection | null>(null);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!actif) return;

    let abandonne = false;
    // `withAutomaticReconnect` ne couvre pas l'échec du tout premier `start` :
    // sans ce minuteur, une API indisponible au chargement de la page laisserait
    // la messagerie hors ligne jusqu'au prochain rechargement.
    let reprise: ReturnType<typeof setTimeout> | null = null;
    let dejaConnecte = false;

    const connexion = new HubConnectionBuilder()
      .withUrl(URL_HUB, {
        // Cf. l'entête du module : le proxy Next ne relaie pas de WebSocket.
        transport:
          HttpTransportType.ServerSentEvents | HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect(politiqueReconnexion)
      .configureLogging(LogLevel.Warning)
      .build();

    connexion.on("MessageRecu", (message: Message) =>
      handlersRef.current.onMessage(message),
    );
    connexion.on("ConversationMiseAJour", ({ idConversation }: { idConversation: number }) =>
      handlersRef.current.onConversationMiseAJour(idConversation),
    );
    connexion.on("Saisie", (evenement: EvenementSaisie) =>
      handlersRef.current.onSaisie(evenement),
    );
    connexion.on("NotificationCourse", (notification: NotificationCourse) =>
      handlersRef.current.onNotificationCourse(notification),
    );

    connexion.onreconnecting(() => setEtat("connexion"));
    connexion.onreconnected(() => {
      setEtat("connecte");
      handlersRef.current.onReconnexion();
    });
    connexion.onclose(() => setEtat("hors-ligne"));

    connexionRef.current = connexion;

    const demarrer = (tentative = 0) => {
      if (abandonne) return;

      setEtat("connexion");

      connexion
        .start()
        .then(() => {
          if (abandonne) return;
          setEtat("connecte");
          // Le premier branchement suit le chargement de la boîte : il n'y a
          // rien à rattraper. Les suivants comblent le trou de la coupure.
          if (dejaConnecte) handlersRef.current.onReconnexion();
          dejaConnecte = true;
        })
        .catch((erreur) => {
          if (abandonne) return;
          setEtat("hors-ligne");
          // Le temps réel est un confort : son échec n'empêche ni de lire ni
          // d'écrire, tout passant par ailleurs en REST.
          console.warn("Messagerie : connexion temps réel impossible", erreur);

          reprise = setTimeout(
            () => demarrer(tentative + 1),
            PALIERS_RECONNEXION[tentative] ?? PALIER_MAX_MS,
          );
        });
    };

    demarrer();

    return () => {
      abandonne = true;
      if (reprise) clearTimeout(reprise);
      connexionRef.current = null;
      setEtat("hors-ligne");
      connexion.stop().catch(() => {
        // Fermeture d'une connexion déjà tombée : sans objet.
      });
    };
  }, [actif]);

  /**
   * Signale que l'utilisateur écrit. Silencieux si le canal est tombé — un
   * indicateur de frappe perdu ne se rattrape pas et ne mérite pas d'erreur.
   */
  const signalerSaisie = (idConversation: number) => {
    const connexion = connexionRef.current;
    if (connexion?.state !== HubConnectionState.Connected) return;

    connexion.invoke("Saisie", idConversation).catch(() => {
      // Idem : l'indicateur est éphémère, son échec est sans conséquence.
    });
  };

  return { etat, signalerSaisie };
};
